/**
 * Creates a faux scrollbar that tracks a scrollable element's scroll position.
 *
 * Manages thumb width/offset via CSS custom properties (--thumb-width, --thumb-offset),
 * toggles an `is-scrolling` class on the scrollbar, and observes resize changes.
 *
 * Call `update()` after programmatic scroll changes.
 */
export function createScrollbar(
  scrollable: HTMLElement,
  scrollbarEl: HTMLElement,
  thumbEl: HTMLElement,
  signal: AbortSignal,
): { update: () => void } {
  let scrollTimer: ReturnType<typeof setTimeout> | undefined;

  const updateThumbWidth = () => {
    const ratio = scrollable.clientWidth / scrollable.scrollWidth;
    scrollbarEl.hidden = ratio >= 1;
    thumbEl.style.setProperty("--thumb-width", String(ratio));
  };

  const updateThumbOffset = () => {
    const maxScroll = scrollable.scrollWidth - scrollable.clientWidth;
    if (maxScroll <= 0) return;

    const trackWidth = scrollbarEl.clientWidth;
    const thumbWidth = trackWidth * (scrollable.clientWidth / scrollable.scrollWidth);
    const maxOffset = trackWidth - thumbWidth;
    const offset = (scrollable.scrollLeft / maxScroll) * maxOffset;
    thumbEl.style.setProperty("--thumb-offset", String(offset));
  };

  const update = () => {
    updateThumbWidth();
    updateThumbOffset();
  };

  const onScroll = () => {
    updateThumbOffset();
    if (scrollbarEl.hidden) return;

    scrollbarEl.classList.add("is-scrolling");
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => scrollbarEl.classList.remove("is-scrolling"), 750);
  };

  update();

  scrollable.addEventListener("scroll", onScroll, { signal, passive: true });

  const resizeObserver = new ResizeObserver(() => update());
  resizeObserver.observe(scrollable);

  signal.addEventListener(
    "abort",
    () => {
      resizeObserver.disconnect();
      clearTimeout(scrollTimer);
    },
    { once: true },
  );

  return { update };
}
